import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // Magic-link landings arrive at /chat#access_token=...&refresh_token=...
  // Supabase parses the hash asynchronously; for a brief window the
  // route renders with isAuthenticated=false BUT the tokens are still
  // sitting in window.location.hash waiting to be consumed. A naive
  // <Navigate to="/" /> at this point would drop the hash entirely
  // and the user would never get signed in.
  const hasHashToken =
    typeof window !== 'undefined' &&
    window.location.hash.includes('access_token=');

  // Show nothing while checking auth status
  if (loading) {
    return (
      <div
        data-testid="protected-loading"
        style={{
          minHeight: '100vh',
          background: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255,255,255,0.3)',
          fontSize: 13,
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
        }}
      >
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    // 1) Magic-link processing in flight — keep the hash, keep waiting.
    if (hasHashToken) {
      return (
        <div
          data-testid="protected-magic-link-waiting"
          style={{
            minHeight: '100vh',
            background: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.3)',
            fontSize: 13,
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
          }}
        >
          Loading...
        </div>
      );
    }

    // 2) Stored Supabase session exists but hasn't hydrated React state
    //    yet — avoid the flicker redirect.
    const allKeys = Object.keys(localStorage);
    const hasPersistedSession = allKeys.some(
      (key) => key.startsWith('sb-') && key.endsWith('-auth-token')
    );
    if (hasPersistedSession) {
      return (
        <div
          data-testid="protected-rehydrating"
          style={{
            minHeight: '100vh',
            background: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.3)',
            fontSize: 13,
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
          }}
        >
          Loading...
        </div>
      );
    }

    // 3) Truly unauthenticated — bounce to landing and pop the modal.
    // Sprint 11.3 (2026-06-13) — instrumented redirect site. If this
    // fires after a successful login, the loop you're seeing is
    // ProtectedRoute reading `isAuthenticated=false` while the user is
    // actually authenticated server-side (token expired locally,
    // session lost between SIGNED_IN and remount, etc.).
    try {
      console.warn('[AUTH-REDIRECT] ProtectedRoute → <Navigate to="/" />',
        { reason: 'isAuthenticated=false AND no persisted sb-* token',
          from: location?.pathname,
          hasUser: !!user });
    } catch (_e) { /* noop */ }
    return <Navigate to="/" state={{ from: location, showAuth: true }} replace />;
  }

  // 4) Authenticated but password not yet set — bounce to /setup-account.
  //    The `needs_password_setup` flag is stamped on the user_metadata
  //    by admin_investor._supabase_create_user_and_link. SetupAccountPage
  //    clears it (both via supabase.auth.updateUser and via the local
  //    flag) once the password is saved.
  //
  //    Google OAuth users are exempt: their identity is already verified
  //    via Google, so even if a returning user has a stale flag, we let
  //    them through without forcing a password.
  const isGoogleUser =
    user?.app_metadata?.provider === 'google' ||
    (user?.identities || []).some((id) => id?.provider === 'google');
  const needsSetup =
    !isGoogleUser &&
    user?.user_metadata?.needs_password_setup === true &&
    (typeof window === 'undefined' ||
      localStorage.getItem('bayenah_password_setup_complete') !== 'true');
  if (needsSetup) {
    return <Navigate to="/setup-account" replace />;
  }

  return children;
};
